/**
 * ParticipationTypeSelector Component
 * Allows selection of participation type when inviting CBOs/individuals
 * Three types: Milestone, Program, Network Partner
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  Target,
  Calendar,
  Network,
  CheckCircle2,
  DollarSign,
  Clock,
  Award,
  Plus,
  X
} from 'lucide-react';

interface Milestone {
  id: number;
  milestone_name: string;
  milestone_type: string;
  has_incentive: boolean;
  incentive_amount?: number;
}

interface MilestoneSelection {
  milestone_id: number;
  milestone_name: string;
  funding_amount: number;
  payout_per_completion: number;
  max_completions?: number;
}

interface ParticipationTypeSelectorProps {
  programId: number;
  onSelectionChange: (selection: {
    participationType: string;
    milestones?: MilestoneSelection[];
    totalFunding: number;
    paymentTerms: string;
    startDate?: string;
    endDate?: string;
  }) => void;
}

export const ParticipationTypeSelector: React.FC<ParticipationTypeSelectorProps> = ({
  programId,
  onSelectionChange
}) => {
  const { t } = useAppTranslation();
  const [participationType, setParticipationType] = useState<string>('PROGRAM');
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [selectedMilestones, setSelectedMilestones] = useState<MilestoneSelection[]>([]);
  const [totalFunding, setTotalFunding] = useState<number>(0);
  const [paymentTerms, setPaymentTerms] = useState<string>('Monthly');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Load program milestones
  useEffect(() => {
    loadMilestones();
  }, [programId]);

  // Notify parent of changes
  useEffect(() => {
    onSelectionChange({
      participationType,
      milestones: participationType === 'MILESTONE' ? selectedMilestones : undefined,
      totalFunding,
      paymentTerms,
      startDate: startDate || undefined,
      endDate: endDate || undefined
    });
  }, [participationType, selectedMilestones, totalFunding, paymentTerms, startDate, endDate]);

  const loadMilestones = async () => {
    try {
      setLoading(true);
      const response = await authFetch(getWeaveinApiUrl(`/api/programs/${programId}/milestones`));
      if (response.ok) {
        const data = await response.json();
        setMilestones(data);
      }
    } catch (err) {
      console.error('Error loading milestones:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleMilestoneSelection = (milestone: Milestone) => {
    const existing = selectedMilestones.find(m => m.milestone_id === milestone.id);
    if (existing) {
      setSelectedMilestones(selectedMilestones.filter(m => m.milestone_id !== milestone.id));
    } else {
      setSelectedMilestones([...selectedMilestones, {
        milestone_id: milestone.id,
        milestone_name: milestone.milestone_name,
        funding_amount: milestone.incentive_amount || 0,
        payout_per_completion: milestone.incentive_amount || 0
      }]);
    }
  };

  const updateMilestoneSelection = (milestoneId: number, field: string, value: number) => {
    setSelectedMilestones(selectedMilestones.map(m =>
      m.milestone_id === milestoneId ? { ...m, [field]: value } : m
    ));
  };

  const participationTypes = [
    {
      id: 'MILESTONE',
      name: 'Milestone Participation',
      description: 'Sign up for specific milestones (outreach, canvassing, etc.)',
      icon: Target,
      color: 'blue',
      paymentModel: 'Pay per milestone completed'
    },
    {
      id: 'PROGRAM',
      name: 'Program Participation',
      description: 'Commit to entire program for a designated period',
      icon: Calendar,
      color: 'green',
      paymentModel: 'Monthly/quarterly payments'
    },
    {
      id: 'NETWORK_PARTNER',
      name: 'Network Partner',
      description: 'Credentialed provider supporting multiple programs',
      icon: Network,
      color: 'purple',
      paymentModel: 'Network-wide agreements'
    }
  ];

  const getColorClasses = (color: string, isSelected: boolean) => {
    const colors: Record<string, { bg: string; border: string; text: string }> = {
      blue: {
        bg: isSelected ? 'bg-blue-50' : 'bg-white',
        border: isSelected ? 'border-blue-500' : 'border-gray-200',
        text: 'text-blue-600'
      },
      green: {
        bg: isSelected ? 'bg-green-50' : 'bg-white',
        border: isSelected ? 'border-green-500' : 'border-gray-200',
        text: 'text-green-600'
      },
      purple: {
        bg: isSelected ? 'bg-purple-50' : 'bg-white',
        border: isSelected ? 'border-purple-500' : 'border-gray-200',
        text: 'text-purple-600'
      }
    };
    return colors[color];
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {t('sponsors.participation_type', 'Participation Type')}
      </label>

      {/* Type Selection */}
      <div className="grid grid-cols-1 gap-3">
        {participationTypes.map(type => {
          const isSelected = participationType === type.id;
          const colors = getColorClasses(type.color, isSelected);
          const Icon = type.icon;

          return (
            <button
              key={type.id}
              onClick={() => setParticipationType(type.id)}
              className={`
                p-4 rounded-lg border-2 text-left transition-all
                ${colors.bg} ${colors.border}
                hover:shadow-md
              `}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${colors.bg}`}>
                  <Icon className={`w-5 h-5 ${colors.text}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">{type.name}</span>
                    {isSelected && (
                      <CheckCircle2 className={`w-5 h-5 ${colors.text}`} />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    <DollarSign className="w-3 h-3 mr-1" />
                    {type.paymentModel}
                  </Badge>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Milestone Selection (for MILESTONE type) */}
      {participationType === 'MILESTONE' && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">{t('sponsors.select_milestones', 'Select Milestones')}</h4>
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('sponsors.loading_milestones', 'Loading milestones...')}</p>
          ) : milestones.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('sponsors.no_milestones_defined_for_this', 'No milestones defined for this program')}</p>
          ) : (
            <div className="space-y-2">
              {milestones.map(milestone => {
                const selected = selectedMilestones.find(m => m.milestone_id === milestone.id);
                return (
                  <div key={milestone.id} className="bg-white dark:bg-gray-800 rounded-lg p-3 border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={() => toggleMilestoneSelection(milestone)}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                        <span className="font-medium text-sm">{milestone.milestone_name}</span>
                        {milestone.has_incentive && (
                          <Badge variant="outline" className="text-xs">
                            ${milestone.incentive_amount}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {selected && (
                      <div className="mt-3 pl-6 grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">{t('sponsors.payout_per_completion', 'Payout per completion')}</label>
                          <Input
                            type="number"
                            value={selected.payout_per_completion}
                            onChange={(e) => updateMilestoneSelection(
                              milestone.id,
                              'payout_per_completion',
                              parseFloat(e.target.value) || 0
                            )}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">{t('sponsors.max_completions', 'Max completions')}</label>
                          <Input
                            type="number"
                            value={selected.max_completions || ''}
                            onChange={(e) => updateMilestoneSelection(
                              milestone.id,
                              'max_completions',
                              parseInt(e.target.value) || 0
                            )}
                            placeholder={t('sponsors.unlimited', 'Unlimited')}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Program/Network Partner Duration */}
      {(participationType === 'PROGRAM' || participationType === 'NETWORK_PARTNER') && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('sponsors.start_date', 'Start Date')}</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('sponsors.end_date', 'End Date')}</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="text-sm"
            />
          </div>
        </div>
      )}

      {/* Funding */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('sponsors.total_funding_allocated', 'Total Funding Allocated')}
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="number"
              value={totalFunding}
              onChange={(e) => setTotalFunding(parseFloat(e.target.value) || 0)}
              className="pl-9 text-sm"
              placeholder="0.00"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('sponsors.payment_terms', 'Payment Terms')}</label>
          <select
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="Per milestone">{t('sponsors.per_milestone', 'Per milestone')}</option>
            <option value="Monthly">{t('sponsors.monthly', 'Monthly')}</option>
            <option value="Quarterly">{t('sponsors.quarterly', 'Quarterly')}</option>
            <option value="Upon completion">{t('sponsors.upon_completion', 'Upon completion')}</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default ParticipationTypeSelector;
